import { getLicensesByCompany } from 'API/company';
import { Roles } from 'enums';
import { useUserData } from 'hooks/use-user-data';
import { useEffect, useRef, useState } from 'react';
import useErrorStore from 'stores/error-store';
import { TenantScopes } from '../account-management.types';
import { License } from 'types/licenses.types';

const useTenantLicenses = (tenantId: string | null, scope: TenantScopes = TenantScopes.LICENSES) => {
  const { role } = useUserData();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [licenses, setLicenses] = useState<License[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);

  const [page, setPage] = useState<number>(1);

  const itemsPerPage = 10;

  const abortController = useRef<AbortController | null>(null);

  const resetAbortController = () => {
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = null;
    }
  };

  const fetchTenantLicenses = async (newPage: number = page, isPagination: boolean = false) => {
    if (!tenantId) return;

    if (isPagination) {
      setPaginationLoading(true);
    } else {
      setLoading(true);
    }
    resetAbortController();

    abortController.current = new AbortController();
    const res = await getLicensesByCompany(tenantId, newPage - 1, itemsPerPage, abortController.current.signal);
    await new Promise((resolve) => setTimeout(resolve, 500));

    if (!res.error) {
      const resLicenses = res.licenses;
      setLicenses(resLicenses);
      setTotal(res.count);
      setPage(newPage);
    } else {
      resetTenantLicenses();
      handleOpenErrorDialog(res?.error, `/company/${tenantId}/licenses`, 'get');
    }
    setLoading(false);
    setPaginationLoading(false);
  };

  const resetTenantLicenses = () => {
    setLicenses([]);
    setTotal(0);
    setPage(1);
  };

  useEffect(() => {
    if (role === Roles.ADMIN || role === Roles.AGENCY || role === Roles.OWNER) {
      if (tenantId && scope === TenantScopes.LICENSES) {
        fetchTenantLicenses(1);
      } else {
        resetTenantLicenses();
      }
    }

    return () => {
      resetAbortController();
      resetTenantLicenses();
    };
  }, [tenantId, scope]);

  /**
   * Pagination handlers
   */

  const handleChangePage = (event: unknown, newPage: number) => {
    fetchTenantLicenses(newPage, true);
  };

  return {
    tenantLicenses: licenses,
    total,
    loading,
    paginationLoading,
    itemsPerPage,
    page,
    handleChangePage,
    fetchTenantLicenses,
    resetTenantLicenses,
  };
};

export default useTenantLicenses;
